/**
 * Sorting and grouping utilities for List View.
 * Uses only the current task data (frontend-only).
 */

import type { Task, TaskStatus } from '@/types';
import type {
  ListViewGroupBy,
  ListViewPreferences,
  ListViewSortDirection,
  ListViewTableSortColumn,
} from './list-view-prefs';
import { getTaskAssignees } from '@/lib/task-assignees';
import { getEffectiveLastUpdated } from '@/lib/task-last-updated';

/** Stable keys for “Last updated” groups (newest → oldest). */
export const LAST_UPDATED_BUCKET_KEYS = [
  '__lu_today',
  '__lu_yesterday',
  '__lu_last7',
  '__lu_last30',
  '__lu_older',
  '__lu_unknown',
] as const;

export type LastUpdatedBucketKey = (typeof LAST_UPDATED_BUCKET_KEYS)[number];

export const LAST_UPDATED_BUCKET_LABELS: Record<LastUpdatedBucketKey, string> = {
  __lu_today: 'Updated today',
  __lu_yesterday: 'Updated yesterday',
  __lu_last7: 'Updated in last 7 days',
  __lu_last30: 'Updated in last 30 days',
  __lu_older: 'Updated earlier',
  __lu_unknown: 'Update time unknown',
};

/**
 * Calendar-day buckets in the user’s local timezone.
 * Today / Yesterday / 2–7 days ago / 8–30 days ago / older / unknown.
 */
export function getLastUpdatedBucketKey(task: Task): LastUpdatedBucketKey {
  const d = getEffectiveLastUpdated(task);
  if (!d) return '__lu_unknown';

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const daysAgo = Math.floor((todayStart.getTime() - taskDayStart.getTime()) / 86_400_000);

  if (daysAgo < 0) return '__lu_today';
  if (daysAgo === 0) return '__lu_today';
  if (daysAgo === 1) return '__lu_yesterday';
  if (daysAgo <= 7) return '__lu_last7';
  if (daysAgo <= 30) return '__lu_last30';
  return '__lu_older';
}

const PRIORITY_ORDER: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function priorityRank(p: string): number {
  return PRIORITY_ORDER[p] ?? 99;
}

function getStatusPosition(task: Task, statuses: TaskStatus[]): number {
  const s = statuses.find((st) => st.id === task.statusId);
  return s?.position ?? 999;
}

/** Compare by assignee display (users joined, then team name); unassigned last. */
function compareAssigneeForSort(a: Task, b: Task): number {
  const usersA = getTaskAssignees(a);
  const usersB = getTaskAssignees(b);
  const unassignedA = usersA.length === 0 && !a.assignedTeamId;
  const unassignedB = usersB.length === 0 && !b.assignedTeamId;
  if (unassignedA && !unassignedB) return 1;
  if (!unassignedA && unassignedB) return -1;
  if (unassignedA && unassignedB) return 0;
  const nameA =
    usersA.length > 0
      ? [...usersA]
          .sort((u, v) => u.displayName.localeCompare(v.displayName))
          .map((u) => u.displayName)
          .join(', ')
      : (a.assignedTeam?.name ?? '');
  const nameB =
    usersB.length > 0
      ? [...usersB]
          .sort((u, v) => u.displayName.localeCompare(v.displayName))
          .map((u) => u.displayName)
          .join(', ')
      : (b.assignedTeam?.name ?? '');
  return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
}

function compareDueDateForTable(a: Task, b: Task, direction: ListViewSortDirection): number {
  const ta = a.dueDate ? new Date(a.dueDate).getTime() : null;
  const tb = b.dueDate ? new Date(b.dueDate).getTime() : null;
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  const raw = ta - tb;
  return direction === 'asc' ? raw : -raw;
}

function getGroupKey(
  task: Task,
  groupBy: ListViewGroupBy,
  _statuses: TaskStatus[]
): string {
  switch (groupBy) {
    case 'status':
      return task.statusId ?? '—';
    case 'assignee': {
      const users = getTaskAssignees(task);
      if (users.length > 0) {
        return [...users].sort((a, b) => a.displayName.localeCompare(b.displayName)).map((u) => u.displayName).join(', ');
      }
      if (task.assignedTeam) return `Team: ${task.assignedTeam.name}`;
      return 'Unassigned';
    }
    case 'priority':
      return task.priority ?? '—';
    case 'tags':
      if (task.tags?.length) return task.tags[0].name;
      return 'No tag';
    case 'dueDate': {
      if (!task.dueDate) return 'No date';
      const d = new Date(task.dueDate);
      return d.toISOString().slice(0, 10);
    }
    case 'list':
      return task.list?.name ?? task.listId ?? '—';
    case 'lastUpdated':
      return getLastUpdatedBucketKey(task);
    case 'none':
    default:
      return '';
  }
}

export function sortTasks(
  tasks: Task[],
  groupBy: ListViewGroupBy,
  direction: ListViewSortDirection,
  statuses: TaskStatus[]
): Task[] {
  if (groupBy === 'none') return [...tasks];

  const mult = direction === 'asc' ? 1 : -1;

  return [...tasks].sort((a, b) => {
    let cmp = 0;

    switch (groupBy) {
      case 'status': {
        const posA = getStatusPosition(a, statuses);
        const posB = getStatusPosition(b, statuses);
        cmp = posA - posB;
        break;
      }
      case 'assignee': {
        cmp = compareAssigneeForSort(a, b);
        break;
      }
      case 'priority': {
        cmp = priorityRank(a.priority) - priorityRank(b.priority);
        break;
      }
      case 'tags': {
        const tagA = a.tags?.[0]?.name ?? '\uFFFF';
        const tagB = b.tags?.[0]?.name ?? '\uFFFF';
        cmp = tagA.localeCompare(tagB);
        break;
      }
      case 'dueDate': {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
        cmp = dateA - dateB;
        break;
      }
      case 'list': {
        const nameA = a.list?.name ?? '';
        const nameB = b.list?.name ?? '';
        cmp = nameA.localeCompare(nameB);
        break;
      }
      case 'lastUpdated': {
        const dateA = getEffectiveLastUpdated(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const dateB = getEffectiveLastUpdated(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        cmp = dateA - dateB;
        break;
      }
      default:
        cmp = 0;
    }

    if (cmp !== 0) return mult * cmp;
    return (a.position - b.position) * mult;
  });
}

export interface ListViewGroup {
  key: string;
  label: string;
  tasks: Task[];
  /** When grouping by status, contains the status info for pill rendering */
  status?: { id: string; name: string; color: string } | null;
}

export function groupTasks(
  tasks: Task[],
  groupBy: ListViewGroupBy,
  statuses: TaskStatus[],
  alsoGroupByList: boolean,
  sortDirection: ListViewSortDirection = 'asc'
): ListViewGroup[] {
  if (groupBy === 'none' && !alsoGroupByList) {
    return [{ key: '_', label: '', tasks: [...tasks] }];
  }

  const map = new Map<string, Task[]>();

  for (const task of tasks) {
    const primary = groupBy !== 'none' ? getGroupKey(task, groupBy, statuses) : '_';
    const secondary = alsoGroupByList ? (task.list?.name ?? task.listId ?? '—') : '_';
    const key = `${primary}\n${secondary}`;
    const list = map.get(key) ?? [];
    list.push(task);
    map.set(key, list);
  }

  const mult = sortDirection === 'asc' ? 1 : -1;
  const groups: ListViewGroup[] = [];
  const entries = Array.from(map.entries()).sort(([a], [b]) => {
    const a0 = a.split('\n')[0];
    const b0 = b.split('\n')[0];
    let cmp = 0;
    if (groupBy === 'status') {
      const statusA = statuses.find((s) => s.id === a0);
      const statusB = statuses.find((s) => s.id === b0);
      // Done statuses always come after active statuses (like ClickUp)
      const isDoneA = statusA?.isDoneState ?? false;
      const isDoneB = statusB?.isDoneState ?? false;
      if (isDoneA !== isDoneB) {
        // Active before done
        cmp = isDoneA ? 1 : -1;
      } else {
        // Within same category, sort by position
        const posA = statusA?.position ?? 999;
        const posB = statusB?.position ?? 999;
        cmp = posA - posB;
      }
    } else if (groupBy === 'priority') {
      cmp = priorityRank(a0) - priorityRank(b0);
    } else if (groupBy === 'lastUpdated') {
      const order = LAST_UPDATED_BUCKET_KEYS;
      cmp = order.indexOf(a0 as LastUpdatedBucketKey) - order.indexOf(b0 as LastUpdatedBucketKey);
      if (cmp === 0) cmp = String(a0).localeCompare(String(b0));
    } else {
      cmp = String(a0).localeCompare(String(b0));
    }
    if (groupBy === 'lastUpdated') {
      return cmp;
    }
    return mult * cmp;
  });

  for (const [key, groupTasks_] of entries) {
    const [primaryKey, subLabel] = key.split('\n');
    let status: { id: string; name: string; color: string } | null = null;
    let displayLabel: string;

    if (groupBy === 'status') {
      const foundStatus = statuses.find((s) => s.id === primaryKey);
      if (foundStatus) {
        status = { id: foundStatus.id, name: foundStatus.name, color: foundStatus.color };
        displayLabel = subLabel && subLabel !== '_' ? `${foundStatus.name} · ${subLabel}` : foundStatus.name;
      } else {
        displayLabel = subLabel && subLabel !== '_' ? `${primaryKey} · ${subLabel}` : primaryKey;
      }
    } else if (groupBy === 'lastUpdated') {
      const luLabel =
        LAST_UPDATED_BUCKET_LABELS[primaryKey as LastUpdatedBucketKey] ?? primaryKey;
      displayLabel = subLabel && subLabel !== '_' ? `${luLabel} · ${subLabel}` : luLabel;
    } else {
      displayLabel = subLabel && subLabel !== '_' ? `${primaryKey} · ${subLabel}` : primaryKey;
    }

    groups.push({ key, label: displayLabel, tasks: groupTasks_, status });
  }

  return groups;
}

export function applyListView(
  tasks: Task[],
  statuses: TaskStatus[],
  prefs: {
    groupBy: ListViewGroupBy;
    sortDirection: ListViewSortDirection;
    alsoGroupByList: boolean;
  }
): ListViewGroup[] {
  const sorted = sortTasks(tasks, prefs.groupBy, prefs.sortDirection, statuses);
  return groupTasks(sorted, prefs.groupBy, statuses, prefs.alsoGroupByList, prefs.sortDirection);
}

/**
 * Applies group-by prefs, then optionally re-sorts tasks inside each group by a table column.
 * Does not fetch data or change which tasks are shown.
 */
export function sortTasksByTableColumn(
  tasks: Task[],
  column: ListViewTableSortColumn,
  direction: ListViewSortDirection,
  statuses: TaskStatus[]
): Task[] {
  const mult = direction === 'asc' ? 1 : -1;
  return [...tasks].sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'name':
        cmp = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
        if (cmp !== 0) return mult * cmp;
        break;
      case 'status': {
        const posA = getStatusPosition(a, statuses);
        const posB = getStatusPosition(b, statuses);
        cmp = posA - posB;
        if (cmp === 0) {
          cmp = (a.status?.name ?? '').localeCompare(b.status?.name ?? '', undefined, {
            sensitivity: 'base',
          });
        }
        if (cmp !== 0) return mult * cmp;
        break;
      }
      case 'assignee':
        cmp = compareAssigneeForSort(a, b);
        if (cmp !== 0) return mult * cmp;
        break;
      case 'dueDate': {
        const c = compareDueDateForTable(a, b, direction);
        if (c !== 0) return c;
        break;
      }
      case 'priority':
        cmp = priorityRank(a.priority) - priorityRank(b.priority);
        if (cmp !== 0) return mult * cmp;
        break;
      case 'updatedAt': {
        const ua = getEffectiveLastUpdated(a)?.getTime() ?? 0;
        const ub = getEffectiveLastUpdated(b)?.getTime() ?? 0;
        cmp = ua - ub;
        if (cmp !== 0) return mult * cmp;
        break;
      }
      default:
        break;
    }
    return (a.position - b.position) * mult;
  });
}

export function applyListViewWithTableColumnSort(
  tasks: Task[],
  statuses: TaskStatus[],
  prefs: ListViewPreferences
): ListViewGroup[] {
  // Filter out closed/done tasks by default, unless showClosedTasks is true
  let filteredTasks = tasks;
  if (!prefs.showClosedTasks) {
    const doneStatusIds = new Set(
      statuses.filter((s) => s.isDoneState).map((s) => s.id)
    );
    filteredTasks = tasks.filter((t) => !doneStatusIds.has(t.statusId));
  }

  const groups = applyListView(filteredTasks, statuses, prefs);
  if (!prefs.tableSortColumn) return groups;
  return groups.map((g) => ({
    ...g,
    tasks: sortTasksByTableColumn(
      g.tasks,
      prefs.tableSortColumn!,
      prefs.tableSortDirection,
      statuses
    ),
  }));
}
